import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronLeft,
  ChevronRight,
  X,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Plus,
  Layers,
  ChevronDown,
  Loader2,
  Play,
  Pause,
  Columns2,
  Film,
  Crop,
  Maximize,
  Edit3,
  Save,
  XCircle,
  Sparkles,
  CheckCircle2,
  Circle,
  Trash2,
  Link,
  Unlink,
} from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getCollectionsQuery, getAssetsQuery } from '@/graphql/queries/getCollections';
import { supabase } from '@/integrations/supabase/client';
import { executeGraphQL } from '@/graphql/client';
import { SideBySideEditor } from '@/components/assets/SideBySideEditor';
import { AnnotationEditor } from '@/components/diffs/AnnotationEditor';
import { useDiffMutations } from '@/hooks/useDiff';
import { formatVersionLabel } from '@/utils/dateUtils';
import { formatTagLabel, type PricingEventTag } from '@/types/pricingEventTags';

// Helper function to parse descriptions with annotation encodings
const parseDescriptionWithAnnotations = (description: string | null | undefined): {
  mainText: string;
  annotations: Array<{ number: string; text: string }>;
} => {
  if (!description) return { mainText: '', annotations: [] };

  // Trim any whitespace
  const trimmedDesc = description.trim();

  // Look for the [[ pattern - find the last occurrence
  const startIndex = trimmedDesc.indexOf('[[');

  if (startIndex === -1) {
    // No annotations found, return full text
    return { mainText: trimmedDesc, annotations: [] };
  }

  // Check if there's a closing ]]
  const endIndex = trimmedDesc.lastIndexOf(']]');

  if (endIndex === -1 || endIndex < startIndex) {
    // No proper closing bracket, return full text
    return { mainText: trimmedDesc, annotations: [] };
  }

  // Extract main text (everything before [[)
  const mainText = trimmedDesc.substring(0, startIndex).trim();

  // Extract annotations text (everything between [[ and ]])
  const annotationsText = trimmedDesc.substring(startIndex + 2, endIndex);

  // Parse individual annotations separated by semicolons
  const annotations = annotationsText
    .split(';')
    .map(annotation => {
      const trimmed = annotation.trim();
      // Extract [n] number and text
      const numberMatch = trimmed.match(/^\[(\d+)\]\s*(.+)$/);
      if (numberMatch) {
        return {
          number: numberMatch[1],
          text: numberMatch[2].trim()
        };
      }
      return null;
    })
    .filter((item): item is { number: string; text: string } => item !== null);

  return { mainText, annotations };
};

// Helper function to format category names for display
const formatCategoryLabel = (category: string | null | undefined): string => {
  if (!category || category === 'null') return 'null';

  // Special cases
  if (category === 'pricing_structure') return 'Pricing Structure';

  // General formatting: replace underscores with spaces and capitalize each word
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to get unique color for each category
const getCategoryColor = (category: string | null | undefined): string => {
  if (!category || category === 'null') return 'bg-red-100 text-red-800 border-red-300';

  const colorMap: Record<string, string> = {
    'pricing_structure': 'bg-purple-100 text-purple-800 border-purple-300',
    'feature': 'bg-blue-100 text-blue-800 border-blue-300',
    'limits': 'bg-orange-100 text-orange-800 border-orange-300',
    'misc': 'bg-gray-100 text-gray-800 border-gray-300',
    'pricing': 'bg-green-100 text-green-800 border-green-300',
    'metric': 'bg-teal-100 text-teal-800 border-teal-300',
    'discount': 'bg-pink-100 text-pink-800 border-pink-300',
    'plan': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  };

  return colorMap[category.toLowerCase()] || 'bg-slate-100 text-slate-800 border-slate-300';
};

interface DiffEventLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  diffId: string;
  initialEventId?: string;
  companySlug: string;
  events: any[];
  onRefresh?: () => void; // Callback to refresh parent data after edits
}

type ViewMode = 'side-by-side' | 'animation';

interface ImagePosition {
  x: number;
  y: number;
}

interface Annotation {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  opacity: number;
  layer?: string;
}

/**
 * Removes crop parameters from a Cloudinary URL while preserving annotations
 * @param url - The Cloudinary URL with crop parameters
 * @returns The URL without crop parameters
 */
const removeCropFromCloudinaryUrl = (url: string): string => {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }

  try {
    // Cloudinary URL format: https://res.cloudinary.com/{cloud_name}/image/upload/{transformations}/{version}/{path}
    const parts = url.split('/upload/');
    if (parts.length !== 2) {
      return url;
    }

    const [baseUrl, afterUpload] = parts;

    // Split the part after 'upload/' to separate transformations from the rest
    const segments = afterUpload.split('/');

    // Find where the actual file path starts (after transformations)
    // Transformations are comma-separated parameters, file path doesn't have commas in this pattern
    let transformationEndIndex = 0;
    for (let i = 0; i < segments.length; i++) {
      // If segment contains transformation syntax (commas or underscore parameters), it's likely a transformation
      if (segments[i].includes(',') || segments[i].match(/^[a-z]_/)) {
        transformationEndIndex = i;
      } else {
        // Once we hit a segment without transformation syntax, we've found the file path
        break;
      }
    }

    // Get transformation segments and file path segments
    const transformationSegments = segments.slice(0, transformationEndIndex + 1);
    const filePathSegments = segments.slice(transformationEndIndex + 1);

    // Process transformations - remove crop-related ones
    const processedTransformations = transformationSegments.map(segment => {
      // Split by comma to get individual transformation parameters
      const params = segment.split(',');

      // Filter out crop-related parameters while preserving annotations
      const filteredParams = params.filter(param => {
        // KEEP: Overlays and annotations (l_, co_, bo_, etc.)
        const isOverlay = param.startsWith('l_'); // Layers/overlays (includes text and rectangles)
        const isColor = param.startsWith('co_'); // Color for overlays
        const isBorder = param.startsWith('bo_'); // Border
        const isEffect = param.startsWith('e_'); // Effects
        const isFlag = param.startsWith('fl_'); // Flags
        const isOpacity = param.startsWith('o_'); // Opacity

        if (isOverlay || isColor || isBorder || isEffect || isFlag || isOpacity) {
          return true; // Keep all annotation-related parameters
        }

        // REMOVE: Crop parameters
        const cropParams = ['c_crop', 'c_fill', 'c_lfill', 'c_fill_pad', 'c_fit', 'c_limit', 'c_mfit', 'c_pad', 'c_lpad', 'c_mpad', 'c_thumb', 'c_imagga_crop', 'c_imagga_scale'];
        const isCropMode = cropParams.some(cp => param.startsWith(cp));
        const isCropDimension = param.startsWith('x_') || param.startsWith('y_') || param.startsWith('w_') || param.startsWith('h_');
        const isCropGravity = param.startsWith('g_') && !param.startsWith('g_auto'); // Keep g_auto, remove crop gravity

        return !isCropMode && !isCropDimension && !isCropGravity;
      });

      return filteredParams.join(',');
    }).filter(segment => segment.length > 0); // Remove empty segments

    // Reconstruct URL
    if (processedTransformations.length > 0) {
      return `${baseUrl}/upload/${processedTransformations.join('/')}/${filePathSegments.join('/')}`;
    } else {
      // No transformations left, return URL without transformation section
      return `${baseUrl}/upload/${filePathSegments.join('/')}`;
    }
  } catch (error) {
    console.error('Error removing crop from Cloudinary URL:', error);
    return url;
  }
}

/**
 * Parses annotation parameters from a Cloudinary URL
 * Annotations use the layer overlay syntax: l_layer,w_,h_,e_colorize,co_rgb:color,o_,bo_,fl_layer_apply,g_north_west,x_,y_
 */
const parseAnnotationFromUrl = (url: string): Annotation | null => {
  if (!url || !url.includes('l_')) return null;

  try {
    // Match the annotation pattern in the URL
    // Pattern: l_LAYER,w_WIDTH,h_HEIGHT,e_colorize,co_rgb:COLOR,o_OPACITY,bo_2px_solid_rgb:COLOR,fl_layer_apply,g_north_west,x_X,y_Y
    const annotationRegex = /l_([^,]+),w_(\d+),h_(\d+),[^,]*e_colorize,co_rgb:([0-9a-fA-F]{6}),[^,]*o_(\d+),[^,]*bo_[^,]*rgb:([0-9a-fA-F]{6}),[^,]*fl_layer_apply,g_north_west,x_(\d+),y_(\d+)/;
    const match = url.match(annotationRegex);

    if (match) {
      return {
        layer: match[1],
        width: parseInt(match[2]),
        height: parseInt(match[3]),
        color: match[4],
        opacity: parseInt(match[5]),
        x: parseInt(match[7]),
        y: parseInt(match[8]),
      };
    }
  } catch (error) {
    console.error('Error parsing annotation from URL:', error);
  }
  return null;
};

/**
 * Transforms annotation coordinates when the crop changes
 * Annotation coordinates are relative to the cropped image, so we need to:
 * 1. Convert annotation to original image coordinates (add old crop offset)
 * 2. Convert to new crop coordinates (subtract new crop offset)
 */
const transformAnnotation = (
  annotation: Annotation,
  oldCrop: ViewportInfo,
  newCrop: ViewportInfo
): Annotation | null => {
  // Convert annotation from old crop space to original image space
  const originalX = oldCrop.imageX + annotation.x;
  const originalY = oldCrop.imageY + annotation.y;

  // Convert from original image space to new crop space
  const newX = originalX - newCrop.imageX;
  const newY = originalY - newCrop.imageY;

  // Check if annotation is completely outside the new crop
  if (newX + annotation.width < 0 || newX > newCrop.imageWidth ||
      newY + annotation.height < 0 || newY > newCrop.imageHeight) {
    console.warn('[transformAnnotation] Annotation is outside new crop bounds, removing it');
    return null;
  }

  // Clip annotation to fit within new crop if partially outside
  const clippedAnnotation = {
    ...annotation,
    x: Math.max(0, newX),
    y: Math.max(0, newY),
    width: Math.min(annotation.width, newCrop.imageWidth - Math.max(0, newX)),
    height: Math.min(annotation.height, newCrop.imageHeight - Math.max(0, newY)),
  };

  console.log('[transformAnnotation] Transformed annotation:', {
    original: annotation,
    oldCrop,
    newCrop,
    originalCoords: { x: originalX, y: originalY },
    newCoords: { x: newX, y: newY },
    clipped: clippedAnnotation,
  });

  return clippedAnnotation;
};

export const DiffEventLightbox: React.FC<DiffEventLightboxProps> = ({
  isOpen,
  onClose,
  diffId: _diffId,
  initialEventId,
  companySlug,
  events,
  onRefresh,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const mutations = useDiffMutations();

  const [currentEventIndex, setCurrentEventIndex] = useState(() => {
    if (initialEventId) {
      const index = events.findIndex(e => e.id === initialEventId);
      return index >= 0 ? index : 0;
    }
    return 0;
  });

  // Current annotation index within the event (for events with multiple annotations/changes)
  const [currentAnnotationIndex, setCurrentAnnotationIndex] = useState(0);
  const [assetData, setAssetData] = useState<any>(null);
  const [loadingAsset, setLoadingAsset] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('side-by-side');
  const [imageType, setImageType] = useState<'cropped' | 'original'>('cropped');
  const [zoom, setZoom] = useState(1);
  const [isAddingToCollection, setIsAddingToCollection] = useState(false);
  const [lastUsedCollectionId, setLastUsedCollectionId] = useState<string>('');

  // Description editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');

  // Verification state - optimistic update
  const [localVerified, setLocalVerified] = useState<boolean | null>(null);

  // Delete event state
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);

  // Side-by-side editor state
  const [isSideBySideEditorOpen, setIsSideBySideEditorOpen] = useState(false);

  // Annotations collapse state
  const [annotationsCollapsed, setAnnotationsCollapsed] = useState(false);

  // Annotation editor state - for editing viewport/marker positions
  const [editedAnnotationCoordinates, setEditedAnnotationCoordinates] = useState<any>(null);
  const [isAnnotationDirty, setIsAnnotationDirty] = useState(false);
  const [isSavingAnnotations, setIsSavingAnnotations] = useState(false);

  // Linked panning state - when true, both images pan together
  const [linkedPan, setLinkedPan] = useState(true);

  // Animation state
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(2500); // milliseconds per flip
  const [showBefore, setShowBefore] = useState(true);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Drag state for side-by-side view
  const [beforeImagePos, setBeforeImagePos] = useState<ImagePosition>({ x: 0, y: 0 });
  const [afterImagePos, setAfterImagePos] = useState<ImagePosition>({ x: 0, y: 0 });
  const [isDraggingBefore, setIsDraggingBefore] = useState(false);
  const [isDraggingAfter, setIsDraggingAfter] = useState(false);
  const [dragStart, setDragStart] = useState<ImagePosition>({ x: 0, y: 0 });
  const beforeImageRef = useRef<HTMLDivElement>(null);
  const afterImageRef = useRef<HTMLDivElement>(null);
  const beforeImgElementRef = useRef<HTMLImageElement>(null);
  const afterImgElementRef = useRef<HTMLImageElement>(null);

  // Fetch collections and assets
  const { data: collectionsResponse } = useQuery({
    queryKey: ['collections'],
    queryFn: () => getCollectionsQuery(),
    enabled: isOpen,
  });

  const { data: assetsResponse, refetch: refetchAssets } = useQuery({
    queryKey: ['assets'],
    queryFn: () => getAssetsQuery(),
    enabled: isOpen,
  });

  const collections = collectionsResponse?.collections || [];

  // Load last used collection from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCollectionId = localStorage.getItem('lastUsedCollection');
      if (savedCollectionId) {
        setLastUsedCollectionId(savedCollectionId);
      }
    }
  }, []);

  // Derive current event and asset IDs from state (don't recalculate on every render)
  const currentEvent = events[currentEventIndex];

  // Get annotation count for current event
  const currentAnnotationCount = React.useMemo(() => {
    if (!currentEvent) return 1;
    const annotations = currentEvent?.annotation_coordinates?.annotations;
    return annotations?.length > 0 ? annotations.length : 1;
  }, [currentEvent?.annotation_coordinates?.annotations]);

  // Calculate total annotations across all events
  const totalAnnotations = React.useMemo(() => {
    return events.reduce((sum, event) => {
      const annotations = event?.annotation_coordinates?.annotations;
      const annotationCount = annotations?.length > 0 ? annotations.length : 1;
      return sum + annotationCount;
    }, 0);
  }, [events]);

  // Calculate current position (1-indexed) across all annotations
  const currentAnnotationPosition = React.useMemo(() => {
    let position = 0;
    // Count all annotations from previous events
    for (let i = 0; i < currentEventIndex; i++) {
      const event = events[i];
      const annotations = event?.annotation_coordinates?.annotations;
      const annotationCount = annotations?.length > 0 ? annotations.length : 1;
      position += annotationCount;
    }
    // Add current annotation position within current event
    position += currentAnnotationIndex + 1;
    return position;
  }, [events, currentEventIndex, currentAnnotationIndex]);

  // Navigation: can go previous if not at first annotation of first event
  const hasPrevious = currentEventIndex > 0 || currentAnnotationIndex > 0;
  // Navigation: can go next if not at last annotation of last event
  const hasNext = currentEventIndex < events.length - 1 || currentAnnotationIndex < currentAnnotationCount - 1;

  // Get asset IDs for current event (memoized to prevent flickering)
  const assetIds = React.useMemo(() => {
    if (!currentEvent) return [];
    if (currentEvent.asset_ids && currentEvent.asset_ids.length > 0) {
      return currentEvent.asset_ids;
    }
    return currentEvent.asset_id ? [currentEvent.asset_id] : [];
  }, [currentEvent?.asset_ids, currentEvent?.asset_id]);

  // Reset to first event when opening
  useEffect(() => {
    if (isOpen) {
      if (initialEventId) {
        const index = events.findIndex(e => e.id === initialEventId);
        setCurrentEventIndex(index >= 0 ? index : 0);
      } else {
        setCurrentEventIndex(0);
      }
      // Reset annotation index
      setCurrentAnnotationIndex(0);
      // Reset positions when opening
      setBeforeImagePos({ x: 0, y: 0 });
      setAfterImagePos({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [isOpen, initialEventId, events]);

  // Animation effect
  useEffect(() => {
    if (viewMode === 'animation' && isPlaying) {
      animationIntervalRef.current = setInterval(() => {
        setShowBefore(prev => !prev);
      }, animationSpeed);
    } else {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    }

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
  }, [viewMode, isPlaying, animationSpeed]);

  // Reset animation when changing view mode
  useEffect(() => {
    if (viewMode !== 'animation') {
      setIsPlaying(false);
      setShowBefore(true);
    }
  }, [viewMode]);

  // Calculate and apply zoom when switching to original image
  const calculateAutoZoom = useCallback(() => {
    if (imageType === 'original') {
      // Set zoom to 300% for original images
      setZoom(3);
      // With transformOrigin 'top center', position (0,0) shows the top of the image
      setBeforeImagePos({ x: 0, y: 0 });
      setAfterImagePos({ x: 0, y: 0 });
    } else if (imageType === 'cropped') {
      // Set zoom to 100% for cropped images
      setZoom(1);
      setBeforeImagePos({ x: 0, y: 0 });
      setAfterImagePos({ x: 0, y: 0 });
    }
  }, [imageType]);

  // Auto-zoom when switching to original image
  useEffect(() => {
    calculateAutoZoom();
  }, [imageType, calculateAutoZoom]);

  // Handle image load to calculate zoom
  const handleImageLoad = useCallback(() => {
    if (imageType === 'original') {
      calculateAutoZoom();
    }
  }, [imageType, calculateAutoZoom]);

  const handlePrevious = useCallback(() => {
    if (!hasPrevious) return;

    if (currentAnnotationIndex > 0) {
      // Go to previous annotation in same event
      setCurrentAnnotationIndex(prev => prev - 1);
    } else if (currentEventIndex > 0) {
      // Go to last annotation of previous event
      const prevEvent = events[currentEventIndex - 1];
      const prevAnnotations = prevEvent?.annotation_coordinates?.annotations;
      const prevAnnotationCount = prevAnnotations?.length > 0 ? prevAnnotations.length : 1;
      setCurrentEventIndex(prev => prev - 1);
      setCurrentAnnotationIndex(prevAnnotationCount - 1);
    }
  }, [hasPrevious, currentAnnotationIndex, currentEventIndex, events]);

  const handleNext = useCallback(() => {
    if (!hasNext) return;

    if (currentAnnotationIndex < currentAnnotationCount - 1) {
      // Go to next annotation in same event
      setCurrentAnnotationIndex(prev => prev + 1);
    } else if (currentEventIndex < events.length - 1) {
      // Go to first annotation of next event
      setCurrentEventIndex(prev => prev + 1);
      setCurrentAnnotationIndex(0);
    }
  }, [hasNext, currentAnnotationIndex, currentAnnotationCount, currentEventIndex, events.length]);

  const handleZoomIn = () => {
    // Allow more zoom for original images (up to 5x), less for cropped (3x)
    const maxZoom = imageType === 'original' ? 5 : 3;
    setZoom(prev => Math.min(prev + 0.25, maxZoom));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    // Reset to appropriate default based on image type
    // Original: 300% (3x), Cropped: 100% (1x)
    const defaultZoom = imageType === 'original' ? 3 : 1;
    setZoom(defaultZoom);
    setBeforeImagePos({ x: 0, y: 0 });
    setAfterImagePos({ x: 0, y: 0 });
  };

  // Drag handlers for Before image
  const handleBeforeMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDraggingBefore(true);
      setDragStart({ x: e.clientX - beforeImagePos.x, y: e.clientY - beforeImagePos.y });
      e.preventDefault();
    }
  };

  const handleBeforeMouseMove = (e: React.MouseEvent) => {
    if (isDraggingBefore && zoom > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Get container and image dimensions for boundary calculation
      const container = beforeImageRef.current;
      const img = beforeImgElementRef.current;

      if (container && img) {
        const containerRect = container.getBoundingClientRect();

        // Get the natural (unscaled) image dimensions
        const naturalWidth = img.naturalWidth || img.width / zoom;
        const naturalHeight = img.naturalHeight || img.height / zoom;

        // Calculate how much of the image is visible in the container
        // With object-contain, the image fits within the container maintaining aspect ratio
        const containerAspect = containerRect.width / containerRect.height;
        const imageAspect = naturalWidth / naturalHeight;

        let displayWidth, displayHeight;
        if (imageAspect > containerAspect) {
          // Image is wider - width constrained
          displayWidth = containerRect.width;
          displayHeight = containerRect.width / imageAspect;
        } else {
          // Image is taller - height constrained
          displayHeight = containerRect.height;
          displayWidth = containerRect.height * imageAspect;
        }

        // Calculate max translation based on zoom
        // When zoomed, the image is larger than the display size
        const scaledWidth = displayWidth * zoom;
        const scaledHeight = displayHeight * zoom;

        // Max translation is half the difference between scaled size and container
        const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
        const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

        const clampedX = Math.max(-maxX, Math.min(maxX, newX));
        const clampedY = Math.max(-maxY, Math.min(maxY, newY));

        setBeforeImagePos({
          x: clampedX,
          y: clampedY,
        });

        // If linked, also update after image position
        if (linkedPan) {
          setAfterImagePos({
            x: clampedX,
            y: clampedY,
          });
        }
      } else {
        setBeforeImagePos({ x: newX, y: newY });
        if (linkedPan) {
          setAfterImagePos({ x: newX, y: newY });
        }
      }
    }
  };

  const handleBeforeMouseUp = () => {
    setIsDraggingBefore(false);
  };

  // Drag handlers for After image
  const handleAfterMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDraggingAfter(true);
      setDragStart({ x: e.clientX - afterImagePos.x, y: e.clientY - afterImagePos.y });
      e.preventDefault();
    }
  };

  const handleAfterMouseMove = (e: React.MouseEvent) => {
    if (isDraggingAfter && zoom > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Get container and image dimensions for boundary calculation
      const container = afterImageRef.current;
      const img = afterImgElementRef.current;

      if (container && img) {
        const containerRect = container.getBoundingClientRect();

        // Get the natural (unscaled) image dimensions
        const naturalWidth = img.naturalWidth || img.width / zoom;
        const naturalHeight = img.naturalHeight || img.height / zoom;

        // Calculate how much of the image is visible in the container
        // With object-contain, the image fits within the container maintaining aspect ratio
        const containerAspect = containerRect.width / containerRect.height;
        const imageAspect = naturalWidth / naturalHeight;

        let displayWidth, displayHeight;
        if (imageAspect > containerAspect) {
          // Image is wider - width constrained
          displayWidth = containerRect.width;
          displayHeight = containerRect.width / imageAspect;
        } else {
          // Image is taller - height constrained
          displayHeight = containerRect.height;
          displayWidth = containerRect.height * imageAspect;
        }

        // Calculate max translation based on zoom
        // When zoomed, the image is larger than the display size
        const scaledWidth = displayWidth * zoom;
        const scaledHeight = displayHeight * zoom;

        // Max translation is half the difference between scaled size and container
        const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
        const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

        const clampedX = Math.max(-maxX, Math.min(maxX, newX));
        const clampedY = Math.max(-maxY, Math.min(maxY, newY));

        setAfterImagePos({
          x: clampedX,
          y: clampedY,
        });

        // If linked, also update before image position
        if (linkedPan) {
          setBeforeImagePos({
            x: clampedX,
            y: clampedY,
          });
        }
      } else {
        setAfterImagePos({ x: newX, y: newY });
        if (linkedPan) {
          setBeforeImagePos({ x: newX, y: newY });
        }
      }
    }
  };

  const handleAfterMouseUp = () => {
    setIsDraggingAfter(false);
  };

  // Calculate asset count for current event
  const assetCount = assetsResponse?.assets?.filter(asset =>
    asset.asset_id === currentEvent?.asset_id
  ).length || 0;

  // Quick add to last used collection - clones the asset with header/subheader from trend
  const handleQuickAdd = async () => {
    const targetCollectionId = lastUsedCollectionId || collections[0]?.id;

    if (!targetCollectionId) {
      toast({
        title: "No Collection",
        description: "Please create a collection first",
        variant: "destructive",
      });
      return;
    }

    setIsAddingToCollection(true);

    // Check if event has an asset_id
    if (!currentEvent.asset_id) {
      toast({
        title: "Error",
        description: "Event has no associated asset",
        variant: "destructive",
      });
      setIsAddingToCollection(false);
      return;
    }

    try {
      // Clone the asset to the collection with header from trendName and subheader from description
      const mutation = `
        mutation CloneAssetToCollection($id: ID!, $collectionId: ID!, $header: String, $subheader: String) {
          cloneAssetToCollection(id: $id, collectionId: $collectionId, header: $header, subheader: $subheader) {
            id
            collection_id
            header
            subheader
          }
        }
      `;

      const result = await executeGraphQL<{ cloneAssetToCollection: { id: string; collection_id: string } }>(
        mutation,
        {
          id: currentEvent.asset_id,
          collectionId: targetCollectionId,
          header: currentEvent.trendName || null, // Use trend name as header
          subheader: currentEvent.description || null // Use event description/hook as subheader
        }
      );

      if (!result.cloneAssetToCollection?.id) {
        throw new Error('Failed to clone asset to collection');
      }

      const clonedAssetId = result.cloneAssetToCollection.id;

      // Update last used collection
      if (typeof window !== 'undefined') {
        localStorage.setItem('lastUsedCollection', targetCollectionId);
        setLastUsedCollectionId(targetCollectionId);
      }

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      refetchAssets();

      const collection = collections.find(c => c.id === targetCollectionId);
      const assetUrl = `/collections/${targetCollectionId}/${clonedAssetId}`;

      toast({
        title: "Asset Cloned to Collection",
        description: (
          <div className="flex flex-col gap-2">
            <p>Cloned to {collection?.name || 'collection'}</p>
            <a
              href={assetUrl}
              className="text-blue-600 hover:text-blue-800 underline font-medium"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = assetUrl;
              }}
            >
              View asset in collection →
            </a>
          </div>
        ),
      });
    } catch (error) {
      console.error('Error cloning asset to collection:', error);
      toast({
        title: "Error",
        description: "Failed to clone asset to collection",
        variant: "destructive",
      });
    } finally {
      setIsAddingToCollection(false);
    }
  };

  // Description editing functions
  const handleStartEditing = () => {
    setEditedDescription(currentEvent.description || '');
    setIsEditingDescription(true);
  };

  const handleCancelEditing = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
    setAiInstructions('');
  };

  const handleSaveDescription = async () => {
    if (!currentEvent.id || editedDescription.trim().length === 0) {
      toast({
        title: "Error",
        description: "Description cannot be empty",
        variant: "destructive",
      });
      return;
    }

    if (editedDescription.length > 280) {
      toast({
        title: "Error",
        description: "Description must be 280 characters or less",
        variant: "destructive",
      });
      return;
    }

    setIsSavingDescription(true);

    try {
      const { error } = await supabase
        .from('app_events')
        .update({ description: editedDescription.trim() })
        .eq('id', currentEvent.id);

      if (error) {
        throw new Error(error.message);
      }

      // Update local state
      currentEvent.description = editedDescription.trim();

      toast({
        title: "Success",
        description: "Event description updated successfully",
      });

      setIsEditingDescription(false);
      setAiInstructions('');
    } catch (error: any) {
      console.error('Error saving description:', error);
      toast({
        title: "Error",
        description: `Failed to save description: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleAICleanup = async () => {
    if (!editedDescription.trim()) {
      toast({
        title: "Error",
        description: "Please enter some text to cleanup",
        variant: "destructive",
      });
      return;
    }

    setIsAIProcessing(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: 'event_description',
            context: editedDescription,
            isRewrite: true, // This triggers cleanup mode
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'AI request failed');
      }

      const data = await response.json();
      setEditedDescription(data.text);
    } catch (error: any) {
      console.error('AI cleanup error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process AI cleanup",
        variant: "destructive",
      });
    } finally {
      setIsAIProcessing(false);
    }
  };

  const handleAIWithInstructions = async () => {
    if (!aiInstructions.trim()) {
      toast({
        title: "Error",
        description: "Please enter instructions for AI",
        variant: "destructive",
      });
      return;
    }

    setIsAIProcessing(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: 'event_description',
            context: editedDescription,
            customInstructions: aiInstructions, // This triggers custom instructions mode
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'AI request failed');
      }

      const data = await response.json();
      setEditedDescription(data.text);
      setAiInstructions('');
    } catch (error: any) {
      console.error('AI instruction error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process AI instructions",
        variant: "destructive",
      });
    } finally {
      setIsAIProcessing(false);
    }
  };

  const handleToggleVerified = async () => {
    if (!currentEvent?.id) return;

    try {
      const currentVerified = localVerified !== null ? localVerified : currentEvent.verified;
      const newVerified = !currentVerified;

      const { error } = await supabase
        .from('app_events')
        .update({ verified: newVerified })
        .eq('id', currentEvent.id);

      if (error) throw error;

      // Optimistic update
      setLocalVerified(newVerified);

      toast({
        title: "Success",
        description: `Event ${newVerified ? 'verified' : 'unverified'}`,
      });
    } catch (error: any) {
      console.error('Error toggling verified:', error);
      toast({
        title: "Error",
        description: `Failed to update: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleDeleteEvent = async () => {
    if (!currentEvent?.id) return;

    setIsDeletingEvent(true);
    try {
      mutations.deleteEvent(currentEvent.id, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Event deleted successfully",
          });

          // Navigate to next event or close if last event
          const remainingEvents = events.filter(e => e.id !== currentEvent.id);
          if (remainingEvents.length === 0) {
            // No more events, close the lightbox
            onClose();
          } else if (currentEventIndex >= remainingEvents.length) {
            // We were on the last event, close the lightbox
            onClose();
          }
          // Otherwise stay at current index (will show next event)

          // Invalidate queries to refresh parent
          queryClient.invalidateQueries(['diff']);
          queryClient.invalidateQueries(['diffs']);
        },
        onError: (error: any) => {
          toast({
            title: "Error",
            description: `Failed to delete event: ${error.message}`,
            variant: "destructive",
          });
        },
        onSettled: () => {
          setIsDeletingEvent(false);
          setDeleteEventDialogOpen(false);
        },
      });
    } catch (error: any) {
      console.error('Error deleting event:', error);
      toast({
        title: "Error",
        description: `Failed to delete event: ${error.message}`,
        variant: "destructive",
      });
      setIsDeletingEvent(false);
      setDeleteEventDialogOpen(false);
    }
  };

  // Reset local verified state when changing events
  useEffect(() => {
    setLocalVerified(null);
  }, [currentEventIndex]);

  // Reset zoom and image mode when navigating between events
  useEffect(() => {
    setZoom(1);
    setImageType('cropped');
    setBeforeImagePos({ x: 0, y: 0 });
    setAfterImagePos({ x: 0, y: 0 });

    // Exit edit mode when switching events
    setIsEditingDescription(false);
    setEditedDescription('');
    setAiInstructions('');

    // Reset annotations to expanded state when changing events
    setAnnotationsCollapsed(false);
  }, [currentEventIndex]);

  // Reset annotation editor state when changing events or annotation index
  useEffect(() => {
    setEditedAnnotationCoordinates(null);
    setIsAnnotationDirty(false);
  }, [currentEventIndex, currentAnnotationIndex]);

  // Reset annotation index and clear asset data when event changes
  useEffect(() => {
    setCurrentAnnotationIndex(0);
    setAssetData(null); // Clear previous asset data immediately
  }, [currentEventIndex]);

  // Fetch asset data when event changes (always use first asset)
  useEffect(() => {
    const loadAsset = async () => {
      const currentAssetId = assetIds[0];
      if (!currentAssetId) {
        setAssetData(null);
        return;
      }

      setLoadingAsset(true);
      try {
        const { data, error } = await supabase
          .from('app_assets')
          .select('*')
          .eq('id', currentAssetId)
          .single();

        if (error) throw error;
        setAssetData(data);
      } catch (error) {
        console.error('Error loading asset:', error);
        setAssetData(null);
      } finally {
        setLoadingAsset(false);
      }
    };

    loadAsset();
  }, [assetIds]);

  const handleOpenEditor = () => {
    // Get original URLs with fallback logic
    const primaryOriginal = currentEvent?.primary_original_url || currentEvent?.asset?.primary_original_url || currentEvent?.primary_cropped_url;
    const secondaryOriginal = currentEvent?.secondary_original_url || currentEvent?.asset?.secondary_original_url || currentEvent?.secondary_cropped_url;

    if (!primaryOriginal || !secondaryOriginal) {
      toast({
        title: "Error",
        description: "Images not available for editing",
        variant: "destructive",
      });
      return;
    }
    setIsSideBySideEditorOpen(true);
  };

  const handleSaveImages = async (url1: string, url2: string) => {
    if (!currentEvent?.asset_id) return;

    try {
      // Update both cropped URLs in database
      const { error } = await supabase
        .from('app_assets')
        .update({
          primary_cropped_url: url1,
          secondary_cropped_url: url2,
        })
        .eq('id', currentEvent.asset_id);

      if (error) throw error;

      // Optimistic update for instant refresh
      currentEvent.primary_cropped_url = url1;
      currentEvent.secondary_cropped_url = url2;

      toast({
        title: "Success",
        description: "Images updated successfully",
      });

      setIsSideBySideEditorOpen(false);

      // Invalidate React Query cache to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['assets'] });

      // Call parent refresh callback if provided
      onRefresh?.();
    } catch (error: any) {
      console.error('Error saving images:', error);
      toast({
        title: "Error",
        description: `Failed to save images: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  // Handle annotation coordinate changes from AnnotationEditor
  const handleAnnotationChange = useCallback((newCoordinates: any) => {
    setEditedAnnotationCoordinates(newCoordinates);
    setIsAnnotationDirty(true);
  }, []);

  // Save annotation coordinates to database
  const handleSaveAnnotations = async () => {
    if (!currentEvent?.id || !editedAnnotationCoordinates) return;

    setIsSavingAnnotations(true);
    try {
      const { error } = await supabase
        .from('app_events')
        .update({
          annotation_coordinates: editedAnnotationCoordinates,
        })
        .eq('id', currentEvent.id);

      if (error) throw error;

      // Optimistic update
      currentEvent.annotation_coordinates = editedAnnotationCoordinates;

      toast({
        title: "Success",
        description: "Annotations saved successfully",
      });

      setIsAnnotationDirty(false);

      // Invalidate cache
      queryClient.invalidateQueries({ queryKey: ['diffs'] });
      onRefresh?.();
    } catch (error: any) {
      console.error('Error saving annotations:', error);
      toast({
        title: "Error",
        description: `Failed to save annotations: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSavingAnnotations(false);
    }
  };

  // Calculate viewport coordinates for crop update
  interface ViewportInfo {
    imageX: number;
    imageY: number;
    imageWidth: number;
    imageHeight: number;
  }

  const calculateViewport = (
    imageRef: React.RefObject<HTMLDivElement>,
    imgElementRef: React.RefObject<HTMLImageElement>,
    imagePos: ImagePosition
  ): ViewportInfo | null => {
    if (!imageRef.current || !imgElementRef.current) return null;

    const container = imageRef.current;
    const img = imgElementRef.current;

    // Get natural image dimensions
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    // Get displayed dimensions (includes zoom)
    const rect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate scale factor from natural to displayed
    const displayScale = rect.width / naturalWidth;

    // Calculate viewport in image coordinates
    const imageX = (containerRect.left - rect.left) / displayScale;
    const imageY = (containerRect.top - rect.top) / displayScale;
    const imageWidth = containerRect.width / displayScale;
    const imageHeight = containerRect.height / displayScale;

    return {
      imageX: Math.max(0, Math.round(imageX)),
      imageY: Math.max(0, Math.round(imageY)),
      imageWidth: Math.min(naturalWidth, Math.round(imageWidth)),
      imageHeight: Math.min(naturalHeight, Math.round(imageHeight)),
    };
  };

  // Build Cloudinary URL with crop transformation and optional annotation
  const buildCroppedUrl = (originalUrl: string, crop: ViewportInfo, annotation?: Annotation | null): string => {
    if (!originalUrl || !originalUrl.includes('cloudinary.com')) {
      return originalUrl;
    }

    try {
      // Parse URL to extract base and asset path
      const parts = originalUrl.split('/upload/');
      if (parts.length !== 2) return originalUrl;

      const [baseUrl, afterUpload] = parts;

      // Remove existing crop transformations from afterUpload
      // Keep other transformations like quality, format, annotations
      const transformParts = afterUpload.split('/');
      const assetPathIndex = transformParts.findIndex(part =>
        part.includes('.') || part.startsWith('v') && /^v\d+$/.test(part)
      );

      let assetPath = afterUpload;
      if (assetPathIndex !== -1) {
        assetPath = transformParts.slice(assetPathIndex).join('/');
      }

      // Build new URL with crop transformation
      const cropTransform = `c_crop,x_${crop.imageX},y_${crop.imageY},w_${crop.imageWidth},h_${crop.imageHeight}`;
      const qualityTransform = 'q_auto,f_auto';

      // Build annotation transformation if provided
      let annotationTransform = '';
      if (annotation) {
        const layerName = annotation.layer || 'one_pixel_gidbyp';
        annotationTransform =
          `l_${layerName},w_${annotation.width},h_${annotation.height},` +
          `e_colorize,co_rgb:${annotation.color},o_${annotation.opacity},` +
          `bo_2px_solid_rgb:${annotation.color},` +
          `fl_layer_apply,g_north_west,x_${annotation.x},y_${annotation.y}`;
      }

      // Construct URL with crop, quality, and annotation (if present)
      if (annotationTransform) {
        return `${baseUrl}/upload/${cropTransform}/${qualityTransform}/${annotationTransform}/${assetPath}`;
      } else {
        return `${baseUrl}/upload/${cropTransform}/${qualityTransform}/${assetPath}`;
      }
    } catch (error) {
      console.error('Error building cropped URL:', error);
      return originalUrl;
    }
  };

  // Handle crop update based on current viewport
  const [isUpdatingCrop, setIsUpdatingCrop] = useState(false);

  // Parse existing crop from Cloudinary URL
  const parseExistingCrop = (url: string): ViewportInfo | null => {
    if (!url || !url.includes('c_crop')) return null;

    try {
      const cropMatch = url.match(/c_crop,x_(\d+),y_(\d+),w_(\d+),h_(\d+)/);
      if (cropMatch) {
        return {
          imageX: parseInt(cropMatch[1]),
          imageY: parseInt(cropMatch[2]),
          imageWidth: parseInt(cropMatch[3]),
          imageHeight: parseInt(cropMatch[4]),
        };
      }
    } catch (error) {
      console.error('Error parsing crop:', error);
    }
    return null;
  };

  const handleUpdateCrop = async () => {
    // Calculate viewport for both images in their CURRENT displayed state
    const beforeViewport = calculateViewport(beforeImageRef, beforeImgElementRef, beforeImagePos);
    const afterViewport = calculateViewport(afterImageRef, afterImgElementRef, afterImagePos);

    if (!beforeViewport || !afterViewport) {
      console.error('[handleUpdateCrop] Failed to calculate viewport');
      toast({
        title: "Error",
        description: "Could not calculate viewport",
        variant: "destructive",
      });
      return;
    }

    // Get current URLs (might be cropped already)
    const currentPrimaryUrl = currentEvent?.primary_cropped_url || currentEvent?.asset?.primary_cropped_url || currentEvent?.primary_original_url || currentEvent?.asset?.primary_original_url || '';
    const currentSecondaryUrl = currentEvent?.secondary_cropped_url || currentEvent?.asset?.secondary_cropped_url || currentEvent?.secondary_original_url || currentEvent?.asset?.secondary_original_url || '';

    if (!currentPrimaryUrl || !currentSecondaryUrl) {
      toast({
        title: "Error",
        description: "Image URLs not found",
        variant: "destructive",
      });
      return;
    }

    // Parse existing crops (if any)
    const existingPrimaryCrop = parseExistingCrop(currentPrimaryUrl);
    const existingSecondaryCrop = parseExistingCrop(currentSecondaryUrl);

    // Parse existing annotations (if any)
    const existingPrimaryAnnotation = parseAnnotationFromUrl(currentPrimaryUrl);
    const existingSecondaryAnnotation = parseAnnotationFromUrl(currentSecondaryUrl);

    console.log('[handleUpdateCrop] Parsed annotations:', {
      existingPrimaryAnnotation,
      existingSecondaryAnnotation,
      existingPrimaryCrop,
      existingSecondaryCrop
    });

    // Calculate new crop coordinates relative to original image
    const newPrimaryCrop: ViewportInfo = existingPrimaryCrop ? {
      imageX: existingPrimaryCrop.imageX + beforeViewport.imageX,
      imageY: existingPrimaryCrop.imageY + beforeViewport.imageY,
      imageWidth: beforeViewport.imageWidth,
      imageHeight: beforeViewport.imageHeight,
    } : beforeViewport;

    const newSecondaryCrop: ViewportInfo = existingSecondaryCrop ? {
      imageX: existingSecondaryCrop.imageX + afterViewport.imageX,
      imageY: existingSecondaryCrop.imageY + afterViewport.imageY,
      imageWidth: afterViewport.imageWidth,
      imageHeight: afterViewport.imageHeight,
    } : afterViewport;

    // Transform annotations if they exist
    let transformedPrimaryAnnotation: Annotation | null = null;
    if (existingPrimaryAnnotation) {
      if (existingPrimaryCrop) {
        // Case 1: Annotation on a cropped image - transform from old crop space to new crop space
        transformedPrimaryAnnotation = transformAnnotation(
          existingPrimaryAnnotation,
          existingPrimaryCrop,
          newPrimaryCrop
        );
      } else {
        // Case 2: Annotation on original image - transform from original space to new crop space
        // The annotation is already in original image coordinates, so we create a dummy "full image" crop
        const fullImageCrop: ViewportInfo = {
          imageX: 0,
          imageY: 0,
          imageWidth: beforeImgElementRef.current?.naturalWidth || 10000,
          imageHeight: beforeImgElementRef.current?.naturalHeight || 10000,
        };
        transformedPrimaryAnnotation = transformAnnotation(
          existingPrimaryAnnotation,
          fullImageCrop,
          newPrimaryCrop
        );
        console.log('[handleUpdateCrop] Transformed annotation from original image:', transformedPrimaryAnnotation);
      }
    }

    let transformedSecondaryAnnotation: Annotation | null = null;
    if (existingSecondaryAnnotation) {
      if (existingSecondaryCrop) {
        // Case 1: Annotation on a cropped image
        transformedSecondaryAnnotation = transformAnnotation(
          existingSecondaryAnnotation,
          existingSecondaryCrop,
          newSecondaryCrop
        );
      } else {
        // Case 2: Annotation on original image
        const fullImageCrop: ViewportInfo = {
          imageX: 0,
          imageY: 0,
          imageWidth: afterImgElementRef.current?.naturalWidth || 10000,
          imageHeight: afterImgElementRef.current?.naturalHeight || 10000,
        };
        transformedSecondaryAnnotation = transformAnnotation(
          existingSecondaryAnnotation,
          fullImageCrop,
          newSecondaryCrop
        );
        console.log('[handleUpdateCrop] Transformed annotation from original image:', transformedSecondaryAnnotation);
      }
    }

    setIsUpdatingCrop(true);

    try {
      // Get original URLs (uncropped) to build from
      const primaryOriginal = currentEvent?.primary_original_url || currentEvent?.asset?.primary_original_url || currentPrimaryUrl;
      const secondaryOriginal = currentEvent?.secondary_original_url || currentEvent?.asset?.secondary_original_url || currentSecondaryUrl;

      // Build new Cloudinary URLs with updated crop and transformed annotations
      const newPrimaryUrl = buildCroppedUrl(primaryOriginal, newPrimaryCrop, transformedPrimaryAnnotation);
      const newSecondaryUrl = buildCroppedUrl(secondaryOriginal, newSecondaryCrop, transformedSecondaryAnnotation);

      // Save to database
      await handleSaveImages(newPrimaryUrl, newSecondaryUrl);

      // Reset zoom to 1 and center position
      setZoom(1);
      setBeforeImagePos({ x: 0, y: 0 });
      setAfterImagePos({ x: 0, y: 0 });

      // Build description based on whether annotations were preserved
      const annotationsPreserved = (transformedPrimaryAnnotation || transformedSecondaryAnnotation) ? ' (annotations preserved)' : '';

      toast({
        title: "Crop Updated",
        description: `Viewport saved as new crop at 100% zoom${annotationsPreserved}`,
      });
    } catch (error: any) {
      console.error('Error updating crop:', error);
      toast({
        title: "Error",
        description: `Failed to update crop: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUpdatingCrop(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable keyboard shortcuts when editing description
      if (isEditingDescription) return;

      // Navigation
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
      // Zoom
      else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      }
      // Toggle linked panning
      else if ((e.key === 'l' || e.key === 'L') && viewMode === 'side-by-side' && zoom > 1) {
        e.preventDefault();
        setLinkedPan(prev => !prev);
      }
      // Update crop
      else if (e.key === 'u' || e.key === 'U') {
        if (zoom > 1 && !isUpdatingCrop) {
          e.preventDefault();
          handleUpdateCrop();
        }
      }
      // View modes
      else if (e.key === '1') {
        e.preventDefault();
        setViewMode('side-by-side');
      } else if (e.key === '2') {
        e.preventDefault();
        setViewMode('animation');
      }
      // Animation controls
      else if (e.key === ' ' && viewMode === 'animation') {
        e.preventDefault();
        if (!isPlaying) {
          setShowBefore(prev => !prev); // Flip immediately when starting
        }
        setIsPlaying(prev => !prev);
      }
      // Arrow keys for manual flip in animation mode
      else if (e.key === 'ArrowDown' && viewMode === 'animation') {
        e.preventDefault();
        setShowBefore(false); // Show After image
        setIsPlaying(false); // Stop animation
      }
      else if (e.key === 'ArrowUp' && viewMode === 'animation') {
        e.preventDefault();
        setShowBefore(true); // Show Before image
        setIsPlaying(false); // Stop animation
      }
      // Add to collection
      else if (e.key === 'c' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleQuickAdd();
      }
      // Close
      else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasPrevious, hasNext, handlePrevious, handleNext, onClose, viewMode, isEditingDescription]);

  if (!isOpen || !currentEvent) {
    return null;
  }

  // Get image URLs from assetData (if navigating between assets) or currentEvent
  // When assetData is loaded, use its URLs; otherwise fall back to currentEvent URLs
  const croppedBeforeUrl = assetData?.primary_cropped_url || currentEvent.primary_cropped_url || '/api/placeholder/800/600';
  const croppedAfterUrl = assetData?.secondary_cropped_url || currentEvent.secondary_cropped_url || '/api/placeholder/800/600';
  const originalBeforeUrl = assetData?.primary_original_url || currentEvent.primary_original_url || croppedBeforeUrl;
  const originalAfterUrl = assetData?.secondary_original_url || currentEvent.secondary_original_url || croppedAfterUrl;

  const beforeImageUrl = imageType === 'cropped' ? croppedBeforeUrl : originalBeforeUrl;
  const afterImageUrl = imageType === 'cropped' ? croppedAfterUrl : originalAfterUrl;

  const getEventTypeBadgeColor = (eventType: string | undefined): string => {
    const type = eventType?.toLowerCase() || '';
    if (type.includes('price')) return 'bg-green-600 text-white';
    if (type.includes('plan')) return 'bg-blue-600 text-white';
    if (type.includes('feature')) return 'bg-purple-600 text-white';
    if (type.includes('discount')) return 'bg-orange-600 text-white';
    if (type.includes('metric')) return 'bg-teal-600 text-white';
    return 'bg-gray-600 text-white';
  };

  // Format date from YYYYMMDD version string to readable format
  // Using shared utility function to ensure consistency across the app
  const formatDate = (dateStr: string): string => {
    if (!dateStr || dateStr.length < 8) return dateStr;
    // Extract just the date portion (first 8 characters) and use shared formatter
    const dateOnly = dateStr.substring(0, 8);
    return formatVersionLabel(dateOnly);
  };

  // Get v_before and v_after from current event (each event has its own dates)
  const vBefore = currentEvent?.v_before;
  const vAfter = currentEvent?.v_after;
  const currentSlug = currentEvent?.slug || companySlug;

  return (
    <>
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()} modal={true}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-[98vw] h-[98vh] p-0 bg-black/95 border-0 shadow-lg duration-200"
          onEscapeKeyDown={onClose}
        >
          <div className="relative w-full h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div className="flex items-center gap-4">
                {currentEvent?.logo_url && (
                  <img
                    src={currentEvent.logo_url}
                    alt={`${currentSlug} logo`}
                    className="h-8 w-8 rounded object-contain flex-shrink-0 bg-white border border-gray-400"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <h2 className="text-xl font-semibold text-white">{currentSlug}</h2>
                <span className="text-sm text-gray-400">
                  {currentAnnotationPosition} of {totalAnnotations}
                  {currentAnnotationCount > 1 && (
                    <span className="ml-2 text-gray-500">
                      (Change {currentAnnotationIndex + 1} of {currentAnnotationCount})
                    </span>
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Image Type Selector - Icon Buttons */}
                <div className="flex items-center gap-1 bg-gray-800 rounded-md p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 ${imageType === 'cropped' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
                    onClick={() => setImageType('cropped')}
                    title="Cropped"
                  >
                    <Crop className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 ${imageType === 'original' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
                    onClick={() => setImageType('original')}
                    title="Original"
                  >
                    <Maximize className="h-4 w-4" />
                  </Button>
                </div>

                {/* View Mode Selector - Icon Buttons */}
                <div className="flex items-center gap-1 bg-gray-800 rounded-md p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 ${viewMode === 'side-by-side' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
                    onClick={() => setViewMode('side-by-side')}
                    title="Side by Side (1)"
                  >
                    <Columns2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 ${viewMode === 'animation' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
                    onClick={() => setViewMode('animation')}
                    title="Animation (2)"
                  >
                    <Film className="h-4 w-4" />
                  </Button>
                </div>

                {/* Verified Checkbox */}
                {(() => {
                  const isVerified = localVerified !== null ? localVerified : (currentEvent?.verified ?? false);
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 ${
                        isVerified
                          ? 'text-green-400 hover:text-green-300 bg-green-900/30'
                          : 'text-white hover:text-white bg-gray-700/50 hover:bg-gray-600'
                      }`}
                      onClick={handleToggleVerified}
                      title={isVerified ? 'Verified - Click to unverify' : 'Not verified - Click to verify'}
                    >
                      {isVerified ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </Button>
                  );
                })()}

                {/* Delete Event Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteEventDialogOpen(true);
                  }}
                  title="Delete event"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>

                {/* Split button for Add to Collection */}
                <div className="flex items-center gap-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleQuickAdd}
                    className="flex items-center gap-2 relative rounded-r-none border-r-0 bg-gray-800 text-white border-gray-700 hover:bg-gray-700"
                    disabled={collections.length === 0 || isAddingToCollection}
                    title="Add to collection (Ctrl+C)"
                  >
                    {isAddingToCollection ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    {lastUsedCollectionId && collections.find(c => c.id === lastUsedCollectionId)
                      ? collections.find(c => c.id === lastUsedCollectionId)?.name
                      : collections[0]?.name || 'Add to Collection'}
                    {assetCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-1 px-1.5 py-0.5 text-xs min-w-[20px] h-5 flex items-center justify-center"
                      >
                        {assetCount}
                      </Badge>
                    )}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="px-2 rounded-l-none bg-gray-800 text-white border-gray-700 hover:bg-gray-700"
                        disabled={collections.length === 0 || isAddingToCollection}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {collections.map((collection) => (
                        <DropdownMenuItem
                          key={collection.id}
                          disabled={isAddingToCollection}
                          onClick={async () => {
                            // Check if event has an asset_id
                            if (!currentEvent.asset_id) {
                              toast({
                                title: "Error",
                                description: "Event has no associated asset",
                                variant: "destructive",
                              });
                              return;
                            }

                            setIsAddingToCollection(true);

                            try {
                              // Clone the asset to the collection with header from trendName and subheader from description
                              const mutation = `
                                mutation CloneAssetToCollection($id: ID!, $collectionId: ID!, $header: String, $subheader: String) {
                                  cloneAssetToCollection(id: $id, collectionId: $collectionId, header: $header, subheader: $subheader) {
                                    id
                                    collection_id
                                    header
                                    subheader
                                  }
                                }
                              `;

                              const result = await executeGraphQL<{ cloneAssetToCollection: { id: string; collection_id: string } }>(
                                mutation,
                                {
                                  id: currentEvent.asset_id,
                                  collectionId: collection.id,
                                  header: currentEvent.trendName || null, // Use trend name as header
                                  subheader: currentEvent.description || null // Use event description/hook as subheader
                                }
                              );

                              if (!result.cloneAssetToCollection?.id) {
                                throw new Error('Failed to clone asset to collection');
                              }

                              const clonedAssetId = result.cloneAssetToCollection.id;

                              // Set as last used collection
                              if (typeof window !== 'undefined') {
                                localStorage.setItem('lastUsedCollection', collection.id);
                                setLastUsedCollectionId(collection.id);
                              }

                              // Invalidate queries
                              queryClient.invalidateQueries({ queryKey: ['collections'] });
                              queryClient.invalidateQueries({ queryKey: ['assets'] });
                              refetchAssets();

                              const assetUrl = `/collections/${collection.id}/${clonedAssetId}`;

                              toast({
                                title: "Asset Cloned to Collection",
                                description: (
                                  <div className="flex flex-col gap-2">
                                    <p>Cloned to {collection.name}</p>
                                    <a
                                      href={assetUrl}
                                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        window.location.href = assetUrl;
                                      }}
                                    >
                                      View asset in collection →
                                    </a>
                                  </div>
                                ),
                              });
                            } catch (error) {
                              console.error('Error cloning asset to collection:', error);
                              toast({
                                title: "Error",
                                description: "Failed to clone asset to collection",
                                variant: "destructive",
                              });
                            } finally {
                              setIsAddingToCollection(false);
                            }
                          }}
                        >
                          {collection.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Close */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={onClose}
                  title="Close (Esc)"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            {/* Event Info Section - Above Images */}
            <div className="px-6 py-4 border-b border-gray-700 bg-black/90">
              <div className="flex flex-col items-center gap-4">
                {/* Category Badge and Date Period */}
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded border text-sm font-medium ${getCategoryColor(currentEvent.category)}`}>
                    {formatCategoryLabel(currentEvent.category)}
                  </span>
                  {vBefore && vAfter && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-gray-800 rounded">
                      <span className="text-xs text-gray-400">Period:</span>
                      <span className="text-sm text-blue-400 font-medium">
                        {formatDate(vBefore)} → {formatDate(vAfter)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Event Description - Inline Editable */}
                {currentEvent.description && (() => {
                  const { mainText, annotations } = parseDescriptionWithAnnotations(currentEvent.description);
                  console.log('[DiffEventLightbox] Parsed description:', {
                    original: currentEvent.description,
                    mainText,
                    annotationsCount: annotations.length
                  });
                  return (
                    <div className="w-full">
                      {!isEditingDescription ? (
                        <div
                          className="flex items-center justify-center gap-2 group cursor-text hover:bg-gray-800/30 rounded-lg px-3 py-2 transition-colors"
                          onClick={handleStartEditing}
                        >
                          <p className="text-lg text-white font-medium text-center flex-1">
                            {mainText}
                          </p>
                        <Edit3 className="h-4 w-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Textarea with inline buttons */}
                        <div className="relative">
                          <textarea
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            className="w-full px-4 py-3 pr-20 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            maxLength={280}
                            placeholder="Enter event description..."
                            autoFocus
                          />
                          {/* Inline action buttons */}
                          <div className="absolute top-2 right-2 flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEditing}
                              disabled={isSavingDescription}
                              className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                              title="Cancel"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleSaveDescription}
                              disabled={isSavingDescription || !editedDescription.trim() || editedDescription.length > 280}
                              className="h-7 w-7 p-0 text-green-400 hover:text-green-300"
                              title="Save"
                            >
                              {isSavingDescription ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                            {editedDescription.length}/280
                          </div>
                        </div>

                        {/* AI Assistance Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-xs text-purple-400 hover:text-purple-300"
                              disabled={isAIProcessing}
                            >
                              {isAIProcessing ? (
                                <>
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  AI Assist
                                </>
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-80">
                            <div className="p-2 space-y-2">
                              <DropdownMenuItem
                                onClick={handleAICleanup}
                                disabled={isAIProcessing || !editedDescription.trim()}
                                className="cursor-pointer"
                              >
                                <Sparkles className="h-4 w-4 mr-2 text-purple-400" />
                                <div>
                                  <div className="font-medium">Cleanup</div>
                                  <div className="text-xs text-gray-500">Improve clarity and grammar</div>
                                </div>
                              </DropdownMenuItem>
                              <div className="px-2 py-1.5 border-t border-gray-700">
                                <div className="text-xs text-gray-400 mb-1.5">Custom instructions:</div>
                                <input
                                  type="text"
                                  value={aiInstructions}
                                  onChange={(e) => setAiInstructions(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey && aiInstructions.trim()) {
                                      e.preventDefault();
                                      handleAIWithInstructions();
                                    }
                                  }}
                                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                  placeholder="e.g., 'make it more concise'"
                                  disabled={isAIProcessing}
                                />
                                {aiInstructions.trim() && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleAIWithInstructions}
                                    disabled={isAIProcessing}
                                    className="mt-1.5 h-7 text-xs w-full text-purple-400 hover:text-purple-300"
                                  >
                                    Apply Instructions
                                  </Button>
                                )}
                              </div>
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                    </div>
                  );
                })()}

                {/* Additional Event Info */}
                {currentEvent.trendName && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-400">Trend:</span>
                    <span className="text-blue-400 font-medium">{currentEvent.trendName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 relative overflow-hidden">
              {/* Loading overlay for asset loading */}
              {loadingAsset && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-10 w-10 text-white animate-spin" />
                    <span className="text-white text-sm">Loading asset...</span>
                  </div>
                </div>
              )}

              {/* Zoom Controls - Only show when NOT using AnnotationEditor (which has its own zoom controls) */}
              {!(viewMode === 'side-by-side' && currentEvent?.annotation_coordinates?.annotations?.length > 0) && (
                <div className="absolute top-4 left-4 z-10 flex flex-col gap-2 bg-black/50 rounded-lg p-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 h-8 w-8"
                    onClick={handleZoomIn}
                    disabled={zoom >= (imageType === 'original' ? 5 : 3)}
                    title="Zoom in (+)"
                  >
                    <ZoomIn className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 h-8 w-8"
                    onClick={handleZoomOut}
                    disabled={zoom <= 0.5}
                    title="Zoom out (-)"
                  >
                    <ZoomOut className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 h-8 w-8"
                    onClick={handleResetZoom}
                    title="Reset zoom (0)"
                  >
                    <Maximize2 className="h-5 w-5" />
                  </Button>
                  <div className="text-white text-xs text-center font-medium mt-1">
                    {Math.round(zoom * 100)}%
                  </div>

                  {/* Link/Unlink button - only show in side-by-side mode and when zoomed */}
                  {viewMode === 'side-by-side' && zoom > 1 && (
                    <>
                      <div className="border-t border-gray-600 my-1" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${linkedPan ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30' : 'text-white hover:bg-white/20'}`}
                        onClick={() => setLinkedPan(!linkedPan)}
                        title={linkedPan ? "Unlink panning (L)" : "Link panning (L)"}
                      >
                        {linkedPan ? <Link className="h-5 w-5" /> : <Unlink className="h-5 w-5" />}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Animation Controls - Only show in animation mode */}
              {viewMode === 'animation' && (
                <div className="absolute top-4 right-4 z-10 bg-black/70 rounded-lg p-4 w-64">
                  <div className="flex items-center gap-3 mb-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20 h-10 w-10"
                      onClick={() => {
                        if (!isPlaying) {
                          setShowBefore(prev => !prev); // Flip immediately when starting
                        }
                        setIsPlaying(prev => !prev);
                      }}
                      title={isPlaying ? "Pause (Space)" : "Play (Space)"}
                    >
                      {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                    </Button>
                    <div className="flex-1">
                      <div className="text-xs text-gray-400 mb-1">Speed</div>
                      <Slider
                        value={[animationSpeed]}
                        onValueChange={(values) => setAnimationSpeed(values[0])}
                        min={200}
                        max={3000}
                        step={100}
                        className="w-full"
                      />
                      <div className="text-xs text-gray-400 mt-1 text-center">
                        {(animationSpeed / 1000).toFixed(1)}s
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-300 text-center">
                    {showBefore ? 'Before' : 'After'}: {showBefore ? formatDate(vBefore) : formatDate(vAfter)}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {hasPrevious && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 text-white hover:bg-white/20"
                  onClick={handlePrevious}
                  title="Previous event (←)"
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
              )}
              {hasNext && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 text-white hover:bg-white/20"
                  onClick={handleNext}
                  title="Next event (→)"
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              )}

              {/* Images */}
              <div className="w-full h-full p-4 flex items-center justify-center overflow-hidden">
                {viewMode === 'side-by-side' && (
                  // Use AnnotationEditor when event has annotation_coordinates with annotations
                  currentEvent?.annotation_coordinates?.annotations?.length > 0 ? (
                    <div className="w-full h-full overflow-auto bg-gray-900 rounded-lg p-4">
                      <AnnotationEditor
                        key={`annotation-editor-${currentEventIndex}-${currentAnnotationIndex}`}
                        annotationCoordinates={
                          editedAnnotationCoordinates || {
                            ...currentEvent.annotation_coordinates,
                            // Show only the current annotation
                            annotations: [currentEvent.annotation_coordinates.annotations[currentAnnotationIndex]]
                          }
                        }
                        initialAnnotationCoordinates={{
                          ...currentEvent.annotation_coordinates,
                          annotations: [currentEvent.annotation_coordinates.annotations[currentAnnotationIndex]]
                        }}
                        primaryOriginalUrl={originalBeforeUrl}
                        secondaryOriginalUrl={originalAfterUrl}
                        onChange={handleAnnotationChange}
                      />
                    </div>
                  ) : (
                    // Fall back to simple image display when no annotations
                    <div className="flex gap-4 h-full w-full">
                      <div className="flex-1 flex flex-col items-center min-w-0 overflow-hidden">
                        <div
                          ref={beforeImageRef}
                          className={`flex-1 w-full flex items-center justify-center overflow-hidden ${zoom > 1 ? 'cursor-move' : 'cursor-default'}`}
                          onMouseDown={handleBeforeMouseDown}
                          onMouseMove={handleBeforeMouseMove}
                          onMouseUp={handleBeforeMouseUp}
                          onMouseLeave={handleBeforeMouseUp}
                        >
                          <img
                            ref={beforeImgElementRef}
                            key={`before-${imageType}-${currentEventIndex}-${currentAnnotationIndex}`}
                            src={beforeImageUrl}
                            alt="Before"
                            className="max-w-full max-h-full object-contain select-none"
                            onLoad={handleImageLoad}
                            style={{
                              transform: `scale(${zoom}) translate(${beforeImagePos.x / zoom}px, ${beforeImagePos.y / zoom}px)`,
                              transformOrigin: 'top center',
                              transition: isDraggingBefore ? 'none' : 'transform 0.1s ease-out'
                            }}
                            draggable={false}
                          />
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col items-center min-w-0 overflow-hidden">
                        <div
                          ref={afterImageRef}
                          className={`flex-1 w-full flex items-center justify-center overflow-hidden ${zoom > 1 ? 'cursor-move' : 'cursor-default'}`}
                          onMouseDown={handleAfterMouseDown}
                          onMouseMove={handleAfterMouseMove}
                          onMouseUp={handleAfterMouseUp}
                          onMouseLeave={handleAfterMouseUp}
                        >
                          <img
                            ref={afterImgElementRef}
                            key={`after-${imageType}-${currentEventIndex}-${currentAnnotationIndex}`}
                            src={afterImageUrl}
                            alt="After"
                            className="max-w-full max-h-full object-contain select-none"
                            onLoad={handleImageLoad}
                            style={{
                              transform: `scale(${zoom}) translate(${afterImagePos.x / zoom}px, ${afterImagePos.y / zoom}px)`,
                              transformOrigin: 'top center',
                              transition: isDraggingAfter ? 'none' : 'transform 0.1s ease-out'
                            }}
                            draggable={false}
                          />
                        </div>
                      </div>
                    </div>
                  )
                )}

                {viewMode === 'animation' && (
                  <div className="flex flex-col items-center h-full relative">
                    <div className="relative w-full h-full flex items-center justify-center">
                      <img
                        key={`${showBefore ? 'before' : 'after'}-${imageType}-${currentEventIndex}-${currentAnnotationIndex}`}
                        src={showBefore ? beforeImageUrl : afterImageUrl}
                        alt={showBefore ? 'Before' : 'After'}
                        className="max-w-full max-h-full object-contain transition-opacity duration-200"
                        style={{
                          transform: `scale(${zoom})`,
                          transformOrigin: 'top center'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Annotations Display - Collapsible */}
              {currentEvent.description && (() => {
                const { annotations } = parseDescriptionWithAnnotations(currentEvent.description);
                return annotations.length > 0 ? (
                  annotationsCollapsed ? (
                    // Collapsed state - small button in footer area
                    <button
                      onClick={() => setAnnotationsCollapsed(false)}
                      className="absolute bottom-4 left-4 z-10 bg-black/80 backdrop-blur-sm rounded-lg px-4 py-2 border border-gray-700 hover:bg-black/90 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-sm text-white">
                        <ChevronDown className="h-4 w-4 rotate-180" />
                        <span className="font-semibold">Annotations</span>
                        <span className="text-xs text-gray-400">({annotations.length})</span>
                      </div>
                    </button>
                  ) : (
                    // Expanded state - full annotations panel
                    <div className="absolute bottom-4 left-4 z-10 max-w-md bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                          <span className="text-gray-400">Image Annotations</span>
                          <span className="text-xs text-gray-500">({annotations.length})</span>
                        </h4>
                        <button
                          onClick={() => setAnnotationsCollapsed(true)}
                          className="text-gray-400 hover:text-white transition-colors"
                          title="Collapse annotations"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {annotations.map((annotation) => (
                          <div key={annotation.number} className="flex gap-2 text-sm">
                            <span className="flex-shrink-0 font-mono text-blue-400 font-semibold">
                              [{annotation.number}]
                            </span>
                            <span className="text-gray-300">{annotation.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ) : null;
              })()}

              {/* Action Buttons - Bottom Right */}
              <div className="absolute bottom-4 right-4 z-10 flex gap-3">
                {/* Save button - shown when annotations are dirty */}
                {isAnnotationDirty && (
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white shadow-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveAnnotations();
                    }}
                    disabled={isSavingAnnotations}
                  >
                    {isSavingAnnotations ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-5 w-5 mr-2" />
                    )}
                    Save
                  </Button>
                )}
                {/* Edit Images button - only shown when not in annotation edit mode or no annotations */}
                {currentEvent?.asset_id && !isAnnotationDirty && !(currentEvent?.annotation_coordinates?.annotations?.length > 0) && (
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEditor();
                    }}
                  >
                    <Edit3 className="h-5 w-5 mr-2" />
                    Edit Images
                  </Button>
                )}
              </div>
            </div>

            {/* Tags Display - Bottom of Lightbox */}
            {currentEvent.tags && currentEvent.tags.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 bg-gray-900/95 border-t border-gray-700 px-6 py-3">
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {currentEvent.tags.map((tag: string) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="text-xs px-2 py-0.5 bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 rounded-sm"
                    >
                      {formatTagLabel(tag as PricingEventTag)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>

    {/* Side-by-Side Image Editor Modal */}
    {currentEvent && isSideBySideEditorOpen && (
      <DialogPrimitive.Root open={isSideBySideEditorOpen} onOpenChange={setIsSideBySideEditorOpen} modal={true}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-[60] translate-x-[-50%] translate-y-[-50%] w-[95vw] h-[95vh] p-0 bg-white border-0 shadow-lg duration-200"
            onEscapeKeyDown={() => setIsSideBySideEditorOpen(false)}
          >
            {/* Close button */}
            <button
              onClick={() => setIsSideBySideEditorOpen(false)}
              className="absolute top-4 right-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            >
              <X className="h-6 w-6 text-gray-700" />
              <span className="sr-only">Close</span>
            </button>

            <SideBySideEditor
              url1={currentEvent.primary_original_url || currentEvent.asset?.primary_original_url || currentEvent.primary_cropped_url || ''}
              url2={currentEvent.secondary_original_url || currentEvent.asset?.secondary_original_url || currentEvent.secondary_cropped_url || ''}
              onSave={handleSaveImages}
              onCancel={() => setIsSideBySideEditorOpen(false)}
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    )}

    {/* Delete Event Confirmation Dialog */}
    <AlertDialog open={deleteEventDialogOpen} onOpenChange={setDeleteEventDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this event?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete the event:
            <br /><br />
            <strong>{currentEvent?.description || 'Untitled event'}</strong>
            <br /><br />
            Event {currentEventIndex + 1} of {events.length} for {companySlug}
            <br /><br />
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeletingEvent}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteEvent}
            disabled={isDeletingEvent}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeletingEvent ? 'Deleting...' : 'Delete Event'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};
